import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LogOut, Plus, Trash2 } from "lucide-react";

const emptyRabbit = { name: "", sex: "Doe", color: "", birth_date: "", price: 125, status: "available", description: "", image_url: "", pedigree: "Pedigreed" };

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("rabbits");
  const [rabbits, setRabbits] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadAll = useCallback(async () => {
    const [r, i, w] = await Promise.all([
      api.get("/rabbits"),
      api.get("/inquiries"),
      api.get("/waitlist"),
    ]);
    setRabbits(r.data);
    setInquiries(i.data);
    setWaitlist(w.data);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveRabbit = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: parseFloat(editing.price) };
    try {
      if (editing.id) {
        await api.put(`/rabbits/${editing.id}`, payload);
        toast.success("Rabbit updated");
      } else {
        await api.post("/rabbits", payload);
        toast.success("Rabbit added");
      }
      setShowForm(false); setEditing(null);
      loadAll();
    } catch { toast.error("Save failed"); }
  };

  const deleteRabbit = async (id) => {
    if (!window.confirm("Delete this rabbit?")) return;
    await api.delete(`/rabbits/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const updateInquiry = async (id, status) => {
    await api.put(`/inquiries/${id}`, { status });
    toast.success("Updated");
    loadAll();
  };

  const removeSubscriber = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    await api.delete(`/waitlist/${id}`);
    toast.success("Removed");
    loadAll();
  };

  const copyEmails = () => {
    const list = waitlist.map((w) => w.email).join(", ");
    navigator.clipboard.writeText(list);
    toast.success(`Copied ${waitlist.length} emails`);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="page-admin">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
          <div>
            <div className="section-label">Admin</div>
            <h1 className="font-serif text-2xl">Rabbitry Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-stone link-underline" data-testid="admin-view-site">View site →</Link>
            <span className="text-xs text-stone">{admin?.email}</span>
            <button onClick={logout} className="flex items-center gap-1 rounded-full border border-bark px-4 py-2 text-xs uppercase tracking-[0.2em] text-bark hover:bg-bark hover:text-cream" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 px-6 lg:px-12">
          {[
            { k: "rabbits", l: `Rabbits (${rabbits.length})` },
            { k: "inquiries", l: `Inquiries (${inquiries.length})` },
            { k: "waitlist", l: `Waitlist (${waitlist.length})` },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`} className={`-mb-px border-b-2 px-4 py-3 text-xs uppercase tracking-[0.2em] transition ${tab === t.k ? "border-terracotta text-terracotta" : "border-transparent text-stone hover:text-foreground"}`}>
              {t.l}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
        {tab === "rabbits" && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-3xl">Rabbits</h2>
              <button onClick={() => { setEditing(emptyRabbit); setShowForm(true); }} data-testid="admin-add-rabbit" className="flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm text-white">
                <Plus size={14} /> Add rabbit
              </button>
            </div>
            <div className="overflow-x-auto border border-border bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted">
                  <tr className="text-left text-xs uppercase tracking-[0.2em] text-stone">
                    <th className="p-4">Name</th><th className="p-4">Sex</th><th className="p-4">Color</th>
                    <th className="p-4">Price</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rabbits.map((r) => (
                    <tr key={r.id} className="border-b border-border last:border-0" data-testid={`admin-rabbit-row-${r.id}`}>
                      <td className="p-4 font-serif text-lg">{r.name}</td>
                      <td className="p-4">{r.sex}</td>
                      <td className="p-4">{r.color}</td>
                      <td className="p-4">${r.price?.toFixed(0)}</td>
                      <td className="p-4 capitalize">{r.status}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`admin-edit-${r.id}`} className="mr-3 text-xs uppercase tracking-[0.2em] text-terracotta">Edit</button>
                        <button onClick={() => deleteRabbit(r.id)} data-testid={`admin-delete-${r.id}`} className="text-stone hover:text-destructive"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  {rabbits.length === 0 && (<tr><td colSpan={6} className="p-12 text-center text-stone">No rabbits yet. Add your first.</td></tr>)}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "inquiries" && (
          <>
            <h2 className="mb-6 font-serif text-3xl">Inquiries</h2>
            <div className="space-y-4">
              {inquiries.map((q) => (
                <div key={q.id} className="border border-border bg-white p-6" data-testid={`admin-inquiry-${q.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="font-serif text-xl">{q.name}</div>
                      <div className="text-xs text-stone">{q.email} {q.phone && `· ${q.phone}`}</div>
                      {q.rabbit_name && <div className="mt-1 text-xs"><span className="text-stone">Interested in:</span> <span className="text-terracotta">{q.rabbit_name}</span></div>}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-block bg-muted px-3 py-1 uppercase tracking-[0.2em]">{q.status}</span>
                      {q.deposit_paid && <span className="inline-block bg-sage px-3 py-1 text-white uppercase tracking-[0.2em]">Deposit paid</span>}
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-foreground/85">{q.message}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {["new", "contacted", "reserved", "closed"].map((s) => (
                      <button key={s} onClick={() => updateInquiry(q.id, s)} data-testid={`admin-inquiry-status-${q.id}-${s}`} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${q.status === s ? "bg-bark text-cream" : "border border-border text-foreground hover:border-bark"}`}>{s}</button>
                    ))}
                  </div>
                </div>
              ))}
              {inquiries.length === 0 && (<div className="border border-border bg-white p-12 text-center text-stone">No inquiries yet.</div>)}
            </div>
          </>
        )}
      </main>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-bark/40 p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveRabbit} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-border bg-white p-8" data-testid="admin-rabbit-form">
            <div className="section-label">{editing.id ? "Edit" : "New"}</div>
            <h3 className="mt-2 font-serif text-3xl">{editing.id ? `Edit ${editing.name}` : "Add a rabbit"}</h3>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Name</label>
                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-form-name" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Sex</label>
                <select value={editing.sex} onChange={(e) => setEditing({ ...editing, sex: e.target.value })} data-testid="admin-form-sex" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm">
                  <option>Doe</option><option>Buck</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Color</label>
                <input required value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} data-testid="admin-form-color" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Birth date</label>
                <input type="date" value={editing.birth_date || ""} onChange={(e) => setEditing({ ...editing, birth_date: e.target.value })} data-testid="admin-form-birth" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Price ($)</label>
                <input required type="number" step="1" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="admin-form-price" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="admin-form-status" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm">
                  <option value="available">Available</option><option value="reserved">Reserved</option><option value="sold">Sold</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Image URL</label>
                <input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} data-testid="admin-form-image" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-stone">Description</label>
                <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="admin-form-description" className="mt-2 w-full border border-border bg-cream px-4 py-3 text-sm" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="submit" data-testid="admin-form-submit" className="rounded-full bg-terracotta px-7 py-3 text-sm text-white">Save</button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} data-testid="admin-form-cancel" className="rounded-full border border-border px-7 py-3 text-sm text-stone">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
